import * as THREE from 'three';

export const BLI_LOGO_SVG_PATH = `${import.meta.env.BASE_URL || '/'}assets/bli-logo.svg`.replace(/\/\/+/g, '/');

const FALLBACK_SVG_DATA_URI =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg width="89" height="143" viewBox="0 0 89 143" fill="none" xmlns="http://www.w3.org/2000/svg">` +
      `<path d="M23.4683 0L6.46826 17L21.4683 32H38.4683V14.5L23.4683 0Z" fill="#00A2B2"/>` +
      `<path d="M82.4701 17.0039L65.4722 0.00184527L50.4703 15L50.4683 32L67.9683 32.0021L82.4701 17.0039Z" fill="#00A2B2"/>` +
      `<path d="M65.4683 76.0297L82.5021 59.0636L67.5319 44.0338L50.5319 44L50.4971 61.5L65.4683 76.0297Z" fill="#00A2B2"/>` +
      `<path d="M6.5901 58.9996L23.4678 76.121L38.5749 61.2289L38.6968 44.2294L21.1972 44.104L6.5901 58.9996Z" fill="#00A2B2"/>` +
      `<path d="M19.74 134.229H5.22V90.7886H21.18C31.44 90.7886 34.32 95.4686 34.32 101.409C34.32 107.409 30.3 110.469 25.68 111.549V111.729C32.76 112.569 35.94 116.409 35.94 122.349C35.94 131.229 29.16 134.229 19.74 134.229ZM12.18 109.269H17.34C21.9 109.269 27.3 108.189 27.3 102.429C27.3 97.2686 24.18 96.4286 18.96 96.4286H12.18V109.269ZM12.18 128.589H18.6C24.36 128.589 28.56 127.569 28.56 121.869C28.56 116.469 24.6 114.969 19.68 114.969H12.18V128.589ZM50.0916 90.7886V128.289H69.2916V134.229H43.0716V90.7886H50.0916ZM83.5486 90.7886V134.229H76.5286V90.7886H83.5486Z" fill="black"/>` +
      `</svg>`
  );

let cachedTexture: THREE.CanvasTexture | null = null;

function renderSvgToCanvas(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
) {
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);
  const naturalW = img.naturalWidth || 89;
  const naturalH = img.naturalHeight || 143;
  const aspect = naturalW / naturalH;

  const pad = canvasWidth * 0.08;
  const maxW = canvasWidth - pad * 2;
  const maxH = canvasHeight - pad * 2;

  let drawW = maxH * aspect;
  let drawH = maxH;
  if (drawW > maxW) {
    drawW = maxW;
    drawH = maxW / aspect;
  }

  const drawX = (canvasWidth - drawW) / 2;
  const drawY = (canvasHeight - drawH) / 2;

  ctx.drawImage(img, drawX, drawY, drawW, drawH);
}

/**
 * Creates and caches the pixel-perfect "BLI" logo texture from /assets/bli-logo.svg.
 * Uses a CanvasTexture with anisotropic filtering so it never degrades when zooming in close.
 */
export function getBliLogoTexture(): THREE.CanvasTexture | null {
  if (cachedTexture) return cachedTexture;
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 16;
  texture.premultiplyAlpha = false;

  if (ctx) {
    // 1. Initial immediate render from data URI so there's no visual flash
    const initialImg = new Image();
    initialImg.onload = () => {
      renderSvgToCanvas(ctx, initialImg, 1024, 1024);
      texture.needsUpdate = true;
    };
    initialImg.src = FALLBACK_SVG_DATA_URI;
    if (initialImg.complete && initialImg.naturalWidth > 0) {
      renderSvgToCanvas(ctx, initialImg, 1024, 1024);
      texture.needsUpdate = true;
    }

    // 2. Load the asset from public/assets/bli-logo.svg
    const svgImg = new Image();
    svgImg.crossOrigin = 'anonymous';
    svgImg.onload = () => {
      renderSvgToCanvas(ctx, svgImg, 1024, 1024);
      texture.needsUpdate = true;
    };
    svgImg.src = BLI_LOGO_SVG_PATH;
    if (svgImg.complete && svgImg.naturalWidth > 0) {
      renderSvgToCanvas(ctx, svgImg, 1024, 1024);
      texture.needsUpdate = true;
    }
  }

  cachedTexture = texture;
  return cachedTexture;
}
